// src/services/apiClient.js
//
// Every call to the Vero backend goes through here, so the bearer token is
// attached in exactly one place rather than remembered at each call site.
//
// The token is an ACCESS token scoped to api://<client-id>/access_as_user, not
// the ID token. See the note in msalConfig.js for why that distinction matters.

import axios from 'axios'
import { getToken } from '@/auth/authService'
import { apiRequest } from '@/auth/msalConfig'
import { getDisplayKey } from '@/auth/displayKey'

const apiClient = axios.create({
  // Relative: the dev server proxies /api to the backend, so the browser stays
  // on one origin. In production the same path is reverse proxied.
  baseURL: '/api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

// Attach the token on the way out.
//
// acquireTokenSilent is called per request rather than caching the string here:
// MSAL already caches it and knows when it has expired, whereas a copy held in
// this module would go stale and start producing 401s an hour after sign-in.
apiClient.interceptors.request.use(async (config) => {
  // A paired screen sends its key and nothing else. It has no Microsoft
  // account, so asking MSAL for a token would only throw. The header name is
  // deliberately not Authorization: the backend checks the two differently
  // and there is no reason to make one look like the other.
  const displayKey = getDisplayKey()
  if (displayKey) {
    config.headers['X-Display-Key'] = displayKey
    return config
  }

  // Pairing is the one call made with no credential at all: the screen has
  // nothing yet, and the code in the body is the credential. Asking MSAL for
  // a token here would throw before the request was ever sent.
  if (config.url === '/PairScreen') return config

  const result = await getToken(apiRequest.scopes)

  if (result?.accessToken) {
    config.headers.Authorization = `Bearer ${result.accessToken}`
  }

  return config
})

// Surface auth failures as something readable rather than a bare 401.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reason = error.response?.data?.message ?? 'not authorised'
      console.warn(`Vero API rejected the request: ${reason}`)
    }
    return Promise.reject(error)
  }
)

export default apiClient
